import React, { useEffect, useState } from 'react'
import { mark } from '../data/record'

// One footer, used by every page (22 Oct 2026: redesigned in the style of
// The Straits Times footer). The static chapter pages carry the same footer
// as plain HTML (claude/scripts in the GAP project), so change both together.
//
// Layout: the page's own film name at the top with "Back to top"; the two
// films side by side (also on a phone) with a dotted rule between; a small
// "Get the next chapter by email" block; icons only; the legal line.

const gapLinks = [
  { label: 'Watch the film', href: '/#watch' },
  { label: 'Screenings', href: '/screenings' },
  { label: 'Be Aware', href: '/climate-awareness' },
  { label: 'Climate Resilience', href: '/climate-resilience' },
  { label: 'The Pianist', href: '/hillary-osullivan' },
  { label: 'Links', href: '/links' },
  { label: 'Contact', href: '/#contact' },
]

const hloLinks = [
  { label: 'About the film', href: '/handslikeours' },
  { label: 'Chapters', href: '/handslikeours/chapters' },
  { label: 'Producer’s diary', href: '/handslikeours/producers-diary' },
]

// Goes to the newest chapter's sign-up box. The server page
// /handslikeours/chapters/latest is rebuilt with the chapters page, so this
// never needs changing when a chapter is added.
const SIGNUP = '/handslikeours/chapters/latest'

const SocialIcons = () => (
  <div className='social-icons'>
    <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Instagram' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <rect x='2' y='2' width='20' height='20' rx='5' ry='5'></rect>
        <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'></path>
        <line x1='17.5' y1='6.5' x2='17.51' y2='6.5'></line>
      </svg>
    </a>
    <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Facebook' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' strokeWidth='0.5' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'></path>
      </svg>
    </a>
    <a href='https://www.youtube.com/@hillaryosullivan' target='_blank' rel='noopener noreferrer' aria-label='YouTube' className='social-icon social-icon--youtube'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
        <path d='M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z'></path>
      </svg>
    </a>
    <a href='https://www.imdb.com/title/tt37798371/' target='_blank' rel='noopener noreferrer' aria-label='IMDb' className='social-icon social-icon--imdb'>IMDb</a>
    <a href='mailto:info@thegirlandherpiano.com' aria-label='Email' className='social-icon'>
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'></path>
        <polyline points='22,6 12,13 2,6'></polyline>
      </svg>
    </a>
  </div>
)

// The footer's own styles, kept here so the component carries everything it needs.
const FOOTER_CSS = `.ft { padding: 2.6rem 16px calc(2.4rem + env(safe-area-inset-bottom, 0px)) !important; }
.ft-in { max-width: 1160px; margin: 0 auto; }
.ft a { color: #E4EAE0; text-decoration: none; }
.ft-top { display: grid; justify-items: center; gap: 0.9rem; padding-bottom: 1.4rem; border-bottom: 1px solid rgba(228,234,224,0.55); }
.ft-word { display: flex; align-items: center; gap: 0.8rem; }
.ft-word img { width: 40px; height: auto; }
.ft-word span { font-family: Oswald, sans-serif; font-weight: 300; font-size: 2rem; letter-spacing: 0.12em; text-transform: uppercase; color: #fff; line-height: 1; }
.ft-up { font-family: 'Quattrocento Sans', sans-serif; font-size: 1rem; }
.ft-up span { display: inline-grid; place-items: center; width: 1.5em; height: 1.5em; border: 1px solid currentColor; border-radius: 50%; margin-left: 0.35em; font-size: 0.85em; line-height: 1; padding-top: 0.25em; box-sizing: border-box; }
.ft-cols { display: grid; grid-template-columns: 1fr 1fr; }
.ft-col { padding: 1.6rem 0 1.4rem; }
.ft-col + .ft-col { border-left: 1px dashed rgba(228,234,224,0.45); padding-left: 1rem; }
.ft-col:first-child { padding-right: 1rem; }
.ft-h { font-family: Oswald, sans-serif !important; font-weight: 400 !important; font-size: 1.02rem !important; letter-spacing: 0.08em !important; text-transform: uppercase; color: #9DBE7A !important; margin: 0 0 0.3rem !important; line-height: 1.25 !important; }
.ft-sub { font-style: italic; font-size: 0.86rem !important; color: rgba(228,234,224,0.7); margin: 0 0 1.1rem !important; }
.ft-col ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.85rem; font-family: 'Quattrocento Sans', sans-serif; font-size: 1.02rem; }
.ft-col a[aria-current] { color: #9DBE7A; }
.ft-sign { border-top: 1px dashed rgba(228,234,224,0.45); padding: 1.3rem 0; gap: 0.7rem !important; text-align: center; display: grid; justify-items: center; gap: 0.9rem; }
.ft-sign p { margin: 0; font-size: 1rem; }
.ft-btn { display: inline-block; border: 1px solid #9DBE7A; color: #9DBE7A !important; font-size: 0.85rem; letter-spacing: 0.04em; padding: 0.4rem 1rem; border-radius: 4px; }
.ft-btn:hover { background: #9DBE7A; color: #0F1F16 !important; }
.ft .social-icons { display: flex; justify-content: center; gap: 1.6rem; padding: 1.5rem 0 1.2rem; border-top: 1px dashed rgba(228,234,224,0.45); margin: 0; }
.ft-legal { text-align: center; font-size: 0.85rem !important; line-height: 1.55; color: rgba(228,234,224,0.8); margin: 0 !important; }
.ft-ph { color: #9DBE7A; }
@media (min-width: 800px) {
  .ft { padding-top: 3.4rem !important; }
  .ft-top { display: flex; justify-content: space-between; align-items: center; padding-bottom: 1.6rem; }
  .ft-word span { font-size: 2.8rem; }
  .ft-word img { width: 54px; }
  .ft-main { display: grid; grid-template-columns: 2fr 1fr; }
  .ft-col { padding: 2rem 2rem 2rem 0; }
  .ft-col + .ft-col { padding-left: 2rem; }
  .ft-sign { border-top: 0; border-left: 1px dashed rgba(228,234,224,0.45); margin: 2rem 0; padding: 0 0 0 2.4rem; text-align: left; justify-items: start; align-content: start; }
  .ft-sign p { font-size: 1.05rem; }
  .ft .social-icons { gap: 2.2rem; padding-top: 1.8rem; }
}
.ft-col a.is-here { color: #9DBE7A; }
.ft-word--gap img { width: 30px; }
@media (min-width: 800px) { .ft-word--gap img { width: 40px; } .ft-word--gap span { font-size: 2.3rem; } }
@media (max-width: 420px) { .ft-word--gap span { font-size: 1.45rem; } }
`

const SiteFooter = ({ film }) => {
  // the page you are on, marked in the footer (read after load, so the
  // server copy and the first paint match)
  const [here, setHere] = useState('')
  useEffect(() => { setHere(window.location.pathname.replace(/\/+$/, '') || '/') }, [])
  const cls = (href) => (here && href === here ? 'is-here' : undefined)
  const cur = (href) => (here && href === here ? 'page' : undefined)
  const isHlo = film === 'hlo'

  const hloCol = (
    <section className='ft-col' key='hlo' aria-label='Hands Like Ours'>
      <h2 className='ft-h'>Hands Like Ours</h2>
      <p className='ft-sub'>A new documentary, filming 2027</p>
      <ul>{hloLinks.map((l) => <li key={l.href}><a href={l.href} className={cls(l.href)} aria-current={cur(l.href)}>{l.label}</a></li>)}</ul>
    </section>
  )
  const gapCol = (
    <section className='ft-col' key='gap' aria-label='The Girl and Her Piano'>
      <h2 className='ft-h'>The Girl and Her Piano</h2>
      <p className='ft-sub'>Short documentary, Bhutan, 2024</p>
      <ul>{gapLinks.map((l) => <li key={l.href}><a href={l.href} className={cls(l.href)} aria-current={cur(l.href)}>{l.label}</a></li>)}</ul>
    </section>
  )

  return (
    <footer className='footer ft'>
      <style>{FOOTER_CSS}</style>
      <div className='ft-in'>
        <div className='ft-top'>
          {isHlo ? (
            <a className='ft-word' href='/handslikeours'>
              <img src='/assets/hlo/hands-like-ours-mark.png' alt='' aria-hidden='true' />
              <span>Hands Like Ours</span>
            </a>
          ) : (
            <a className='ft-word ft-word--gap' href='/'>
              <img src={mark} alt='' aria-hidden='true' />
              <span>The Girl and Her Piano</span>
            </a>
          )}
          <a className='ft-up' href='#top' onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}>Back to top <span aria-hidden='true'>&#8963;</span></a>
        </div>
        <div className='ft-main'>
          <div className='ft-cols'>
            {isHlo ? [hloCol, gapCol] : [gapCol, hloCol]}
          </div>
          <div className='ft-sign'>
            <p>Get the next chapter by email.</p>
            <a className='ft-btn' href={SIGNUP}>Sign up</a>
          </div>
        </div>
        <SocialIcons />
        <p className='ft-legal'>&copy; 2026 White Room Studio Pte Ltd, Singapore. All rights reserved.</p>
      </div>
    </footer>
  )
}

export default SiteFooter
